import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const CART_KEY = "vornx-cart";
const CartContext = createContext(null);

const lineKey = (item) => `${item.product_id}::${item.variant_id || "default"}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(window.localStorage.getItem(CART_KEY)) || [];
    } catch (_) {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, quantity = 1, variant = null) => {
    const newItem = {
      product_id: product.id,
      slug: product.slug,
      sku: product.sku,
      name: product.name,
      image: (product.images || [])[0] || null,
      price: variant?.price != null ? variant.price : product.price,
      capacity: product.capacity,
      fragrance: product.fragrance,
      line: product.line,
      is_discovery: !!product.is_discovery,
      variant_id: variant?.id || null,
      variant_label: variant?.label || null,
      quantity,
    };
    // Discovery Set = prodotto-assaggio: si acquista SOLO da solo
    let blocked = null;
    setItems((prev) => {
      const hasDiscovery = prev.some((i) => i.is_discovery);
      const hasOther = prev.some((i) => !i.is_discovery);
      if (newItem.is_discovery && hasOther) { blocked = "alone"; return prev; }
      if (!newItem.is_discovery && hasDiscovery) { blocked = "blocks"; return prev; }
      const key = lineKey(newItem);
      const existing = prev.find((i) => lineKey(i) === key);
      if (existing) {
        return prev.map((i) => (lineKey(i) === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, newItem];
    });
    if (blocked === "alone") {
      toast.error("Il Discovery Set si acquista singolarmente. Svuota il carrello per aggiungerlo.");
    } else if (blocked === "blocks") {
      toast.error("Hai il Discovery Set nel carrello: si acquista da solo. Completa o svuota il carrello per aggiungere altri prodotti.");
    } else {
      setOpen(true);
    }
  }, []);

  const updateQty = useCallback((key, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (lineKey(i) === key ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => lineKey(i) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, count, subtotal, open, setOpen, addItem, updateQty, removeItem, clear, lineKey }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
